import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchMedicationSheet } from "../../../store/slices/medicationSheetsSlice";
import PrintWrapper, { printFooterStyles } from "../../common/PrintWrapper";
import logo from "../../../assets/images/logo-samplee.png";

export default function MedicationSheetPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { currentMedicationSheet, isLoading, error } = useSelector(
    (state) => state.medicationSheets || {}
  );

  useEffect(() => {
    if (id) dispatch(fetchMedicationSheet(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (currentMedicationSheet) {
      const timer = setTimeout(() => window.print(), 1000);
      window.onafterprint = () => navigate(-1);
      return () => {
        clearTimeout(timer);
        window.onafterprint = null;
      };
    }
  }, [currentMedicationSheet, navigate]);

  if (isLoading) return <div className="p-8 text-center">Loading Medication Sheet...</div>;
  if (error) return <div className="p-8 text-danger text-center">{error}</div>;
  if (!currentMedicationSheet) return <div className="p-8 text-center">No record found.</div>;

  const d = currentMedicationSheet;
  const medications = d.items || [];

  // Helper to format Date (Force UTC to prevent date shifting)
  const fmtDate = (dateStr) =>
    dateStr ? new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "UTC" // Add this line
    }) : "-";

  // Helper to format Time only (Force UTC to match API response)
  const fmtTime = (dateTimeStr) =>
    dateTimeStr ? new Date(dateTimeStr).toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "UTC" // Add this line
    }) : "-";

  return (
    <>
      <style jsx global>{`
        * { box-sizing: border-box !important; }
        body { font-family: 'Segoe UI', Arial, sans-serif !important; background: #fff !important; color: #000 !important; font-size: 12px !important; margin: 0 !important; padding: 0 !important; }
        .print-container { padding: 30px !important; position: relative !important; }
        ${printFooterStyles}
        
        .logo { position: absolute !important; top: 15px !important; left: 20px !important; }
        .logo img { height: 60px !important; width: auto !important; }

        .header { text-align: center !important; margin-bottom: 25px !important; border-bottom: 2px solid #000 !important; padding-bottom: 10px !important; }
        .header h3 { font-size: 1.6rem !important; font-weight: bold !important; margin-bottom: 4px !important; }
        .header h6 { font-size: 1rem !important; letter-spacing: 1px !important; }

        .patient-info { display: grid !important; grid-template-columns: repeat(3, 1fr) !important; gap: 10px !important; margin-bottom: 20px !important; border: 1px solid #000 !important; padding: 10px !important; background: #f9f9f9 !important; }
        .info-item { margin-bottom: 5px !important; }
        .info-item strong { text-transform: uppercase !important; font-size: 10px !important; color: #444 !important; }
        .underline { border-bottom: 1px solid #000 !important; font-weight: bold !important; display: inline-block !important; min-width: 100px !important; margin-left: 5px !important; }

        .med-table { width: 100% !important; border-collapse: collapse !important; margin-top: 10px !important; table-layout: fixed !important; }
        .med-table th, .med-table td { border: 1px solid #000 !important; padding: 8px 5px !important; vertical-align: top !important; }
        .med-table th { background: #eee !important; font-weight: bold !important; text-align: center !important; }
        
        .drug-col { width: 25% !important; }
        .entry-col { width: 75% !important; padding: 0 !important; }

        .inner-entry-grid { display: grid !important; grid-template-columns: repeat(auto-fill, minmax(100px, 1fr)) !important; gap: 0 !important; }
        .entry-box { border-right: 0.5px solid #ccc !important; border-bottom: 0.5px solid #ccc !important; padding: 5px !important; text-align: center !important; min-height: 50px !important; }
        .entry-box:last-child { border-right: none !important; }
        
        .status-badge { font-weight: bold !important; font-size: 14px !important; display: block !important; }
        .time-label { font-size: 9px !important; color: #555 !important; }

        .footer-meta { margin-top: 30px !important; border-top: 1px solid #000 !important; padding-top: 15px !important; display: grid !important; grid-template-columns: 1fr 1fr !important; gap: 20px !important; }
        .meta-row { margin-bottom: 10px !important; }
        
        @media print {
            .med-table { page-break-inside: auto !important; }
            tr { page-break-inside: avoid !important; page-break-after: auto !important; }
        }
      `}</style>

      <PrintWrapper>
        <div className="print-container">
        <div className="logo"><img src={logo} alt="Logo" /></div>
        <div className="header">
          <h3>THE RECOVERY HOUSE</h3>
          <h6>MEDICATION ADMINISTRATION SHEET</h6>
        </div>

        <div className="patient-info">
          <div className="info-item"><strong>Name:</strong> <span className="underline">{d.name}</span></div>
          {/* <div className="info-item"><strong>Bed No:</strong> <span className="underline">{d.bed_no}</span></div> */}
          <div className="info-item"><strong>MR No:</strong> <span className="underline">{d.mr_no}</span></div>
          <div className="info-item"><strong>Age:</strong> <span className="underline">{d.age}</span></div>
          <div className="info-item"><strong>D.O.A:</strong> <span className="underline">{fmtDate(d.doa)}</span></div>
          <div className="info-item"><strong>Consultant:</strong> <span className="underline">{d.consultant}</span></div>
          {/* <div className="info-item" style={{ gridColumn: 'span 2' }}><strong>Diagnosis:</strong> <span className="underline">{d.diagnosis}</span></div> */}
          {/* <div className="info-item"><strong>Ward:</strong> <span className="underline">{d.ward}</span></div> */}
        </div>

        <table className="med-table">
          <thead>
            <tr>
              <th className="drug-col">Drug Details</th>
              <th className="entry-col">Administration Log (Time, Date & Status)</th>
            </tr>
          </thead>
          <tbody>
            {medications.map((med) => (
              <tr key={med.id}>
                <td className="drug-col">
                  <div style={{ fontWeight: 'bold', fontSize: '13px', marginBottom: '5px' }}>{med.drug_name}</div>
                  <div style={{ fontSize: '11px', marginBottom: '2px' }}>Dose: {med.dose || "-"}</div>
                  <div style={{ fontSize: '11px', marginBottom: '5px' }}>Frequency: {med.frequency || "-"}</div>
                  {/* <div style={{ fontStyle: 'italic', marginBottom: '5px' }}>Sig: {med.sign}</div> */}
                  <div className="time-label">Started: {fmtDate(med.start_date)}</div>
                </td>
                <td className="entry-col">
                  <div className="inner-entry-grid">
                    {med.time_entries && med.time_entries.length > 0 ? (
                      med.time_entries.map((entry) => (
                        <div key={entry.id} className="entry-box">
                          <span className="time-label">{fmtDate(entry.time_date)}</span>
                          <span style={{ fontWeight: 'bold', display: 'block' }}>{fmtTime(entry.time_date)}</span>
                          <span className="status-badge" style={{ color: entry.given === "Yes" ? "green" : "red" }}>
                            {entry.given === "Yes" ? "✓" : "✘"}
                          </span>
                          <span style={{ fontSize: '8px', textTransform: 'uppercase' }}>{entry.given === "Yes" ? "Given" : "Not Given"}</span>
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: '10px', color: '#999' }}>No entries recorded</div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="footer-meta">
          <div>
            <div className="meta-row"><strong>Document Name:</strong> <span className="underline">{d.document_name}</span></div>
            <div className="meta-row"><strong>Effective Date:</strong> <span className="underline">{fmtDate(d.effective_date)}</span></div>
            <div className="meta-row"><strong>Approved By:</strong> <span className="underline">{d.approved_by}</span></div>
          </div>
          <div>
            <div className="meta-row"><strong>Approved Date:</strong> <span className="underline">{fmtDate(d.approved_date)}</span></div>
            <div className="meta-row"><strong>Next Revision By:</strong> <span className="underline">{d.next_revision_by}</span></div>
            <div className="meta-row"><strong>Next Revision Date:</strong> <span className="underline">{fmtDate(d.next_revision_date)}</span></div>
          </div>
        </div>

        <div style={{ marginTop: '50px', borderTop: '1px solid #000', paddingTop: '10px', textAlign: 'center', fontSize: '10px' }}>
          <p>156 Bangalore Town, Tipu Sultan Road Karachi-Pakistan | Ph : 021-34546364-65-66</p>
          <p><strong>A Project of “CARVAN OF LIFE PAKISTAN TRUST”</strong></p>
        </div>
      </div>
      </PrintWrapper>
    </>
  );
}